import { useState } from 'react'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SavingsIcon from '@mui/icons-material/Savings'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

function LandingROICalculator () {
  const [roiInputs, setRoiInputs] = useState({
    technicians: 20,
    hourlyRate: 50,
    hoursPerWeek: 2
  })

  const roiPresets = [
    { name: 'Kleines Team', technicians: 10, hourlyRate: 40, hoursPerWeek: 1.5 },
    { name: 'Mittelständisch', technicians: 20, hourlyRate: 50, hoursPerWeek: 2 },
    { name: 'Großunternehmen', technicians: 50, hourlyRate: 60, hoursPerWeek: 3 }
  ]

  const applyROIPreset = (preset) => {
    setRoiInputs(preset)
  }

  const calculateROI = () => {
    const { technicians, hourlyRate, hoursPerWeek } = roiInputs
    const pricePerUser = technicians < 25 ? 28 : 25
    const monthlyCost = technicians * pricePerUser
    const monthlyHoursSaved = hoursPerWeek * 4 * technicians
    const monthlySavings = monthlyHoursSaved * hourlyRate
    const netSavings = monthlySavings - monthlyCost
    const roi = ((netSavings / monthlyCost) * 100).toFixed(0)
    const breakEvenDays = Math.ceil((monthlyCost / monthlySavings) * 30)

    return {
      pricePerUser,
      monthlyCost,
      monthlyHoursSaved,
      monthlySavings,
      roi,
      breakEvenDays
    }
  }

  const roiData = calculateROI()

  return (
    <section className='lp-roi-calculator-section'>
      <div className='lp-section-content'>
        <div className='lp-roi-section'>
          <div className='lp-roi-header'>
            <TrendingUpIcon sx={{ fontSize: 40 }} />
            <h3>Berechnen Sie Ihren ROI</h3>
          </div>
          <p className='lp-roi-subtitle'>
            Passen Sie die Werte an Ihr Unternehmen an und sehen Sie die
            Einsparungen in Echtzeit
          </p>

          {/* Preset Buttons */}
          <div className='lp-roi-presets'>
            <span className='lp-presets-label'>Beispiel-Konfigurationen:</span>
            <div className='lp-presets-buttons'>
              {roiPresets.map((preset, index) => (
                <button
                  key={index}
                  className='lp-preset-button'
                  onClick={() => applyROIPreset(preset)}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Inputs */}
          <div className='lp-roi-inputs'>
            <div className='lp-roi-input-group'>
              <label htmlFor='technicians'>
                Anzahl Servicetechniker
                <span className='lp-input-value'>{roiInputs.technicians}</span>
              </label>
              <input
                type='range'
                id='technicians'
                min='1'
                max='100'
                value={roiInputs.technicians}
                onChange={e =>
                  setRoiInputs({
                    ...roiInputs,
                    technicians: parseInt(e.target.value)
                  })
                }
                className='lp-roi-slider'
              />
            </div>

            <div className='lp-roi-input-group'>
              <label htmlFor='hourlyRate'>
                Durchschnittlicher Stundensatz (€)
                <span className='lp-input-value'>{roiInputs.hourlyRate}€</span>
              </label>
              <input
                type='range'
                id='hourlyRate'
                min='20'
                max='150'
                step='5'
                value={roiInputs.hourlyRate}
                onChange={e =>
                  setRoiInputs({
                    ...roiInputs,
                    hourlyRate: parseInt(e.target.value)
                  })
                }
                className='lp-roi-slider'
              />
            </div>

            <div className='lp-roi-input-group'>
              <label htmlFor='hoursPerWeek'>
                Zeitersparnis pro Techniker/Woche (Stunden)
                <span className='lp-input-value'>{roiInputs.hoursPerWeek}h</span>
              </label>
              <input
                type='range'
                id='hoursPerWeek'
                min='0.5'
                max='10'
                step='0.5'
                value={roiInputs.hoursPerWeek}
                onChange={e =>
                  setRoiInputs({
                    ...roiInputs,
                    hoursPerWeek: parseFloat(e.target.value)
                  })
                }
                className='lp-roi-slider'
              />
            </div>
          </div>

          {/* Results */}
          <div className='lp-roi-grid'>
            <div className='lp-roi-card lp-roi-cost'>
              <SavingsIcon sx={{ fontSize: 32 }} />
              <div className='lp-roi-label'>Monatliche Kosten</div>
              <div className='lp-roi-value'>
                {roiData.monthlyCost.toLocaleString('de-DE')}€
              </div>
              <div className='lp-roi-detail'>
                {roiInputs.technicians} Techniker × {roiData.pricePerUser}€
              </div>
            </div>

            <div className='lp-roi-arrow'>→</div>

            <div className='lp-roi-card lp-roi-savings'>
              <TrendingUpIcon sx={{ fontSize: 32 }} />
              <div className='lp-roi-label'>Monatliche Einsparung</div>
              <div className='lp-roi-value'>
                {roiData.monthlySavings.toLocaleString('de-DE')}€
              </div>
              <div className='lp-roi-detail'>
                {roiInputs.hoursPerWeek}h/Woche = {roiData.monthlyHoursSaved}
                h/Monat × {roiInputs.hourlyRate}€
              </div>
            </div>

            <div className='lp-roi-arrow'>→</div>

            <div className='lp-roi-card lp-roi-result'>
              <CheckCircleIcon sx={{ fontSize: 32 }} />
              <div className='lp-roi-label'>ROI</div>
              <div className='lp-roi-value'>{roiData.roi}%</div>
              <div className='lp-roi-detail'>
                Break-even nach {roiData.breakEvenDays}{' '}
                {roiData.breakEvenDays === 1 ? 'Tag' : 'Tagen'}
              </div>
            </div>
          </div>

          <div className='lp-roi-note'>
            * Interaktive Berechnung basierend auf Ihren Angaben. Tatsächliche
            Einsparungen können variieren.
          </div>
        </div>
      </div>
    </section>
  )
}

export default LandingROICalculator

